import React, { useState } from 'react';

import { Box, Text } from '@clutter/clean';

import { useTrack } from '@root/initializers/wt';

import { ButtonCTA } from '@shared/button_cta';
import { useClientDataContext } from '@shared/client_data_context';
import { Block, BlockProps } from '@shared/page_builder/block';
import { ServiceSelection } from '@shared/service_selector/constants';
import { TabContainer } from '@shared/tab_container';
import { TabItem } from '@shared/tab_item';

import { useEffectAfterMount } from '@utils/hooks/use_effect_after_mount';

import locationTruckTracking from '@images/illustrations/location_truck_tracking.svg';
import moverPackingBoxes from '@images/illustrations/mover_packing_boxes.svg';
import moverWithCart from '@images/illustrations/mover_with_cart_and_boxes.svg';
import moverWithFridge from '@images/illustrations/mover_with_refrigerator.svg';
import moverWithTruck from '@images/illustrations/mover_with_sixteen_foot_truck.svg';
import photoCatalog from '@images/illustrations/online_photo_catalog_small.svg';
import storageFacility from '@images/illustrations/storage_facility.svg';
import wrappedPackedItems from '@images/illustrations/wrapped_packed_items.svg';

type ServiceIllustration = {
  title: string;
  description: React.ReactNode;
  src: string;
};

const STORAGE_STEPS: ServiceIllustration[] = [
  {
    title: 'Home pickup',
    description: (
      <>
        We’ll pick up your items
        <br />
        &mdash; furniture too!
      </>
    ),
    src: moverWithCart,
  },
  {
    title: 'Packing',
    description: <>We’ll professionally and safely pack your boxes.</>,
    src: moverPackingBoxes,
  },
  {
    title: 'Storage',
    description: (
      <>
        We’ll securely store your items and upload photos of your items to your
        account.
      </>
    ),
    src: storageFacility,
  },
  {
    title: 'Delivery',
    description: (
      <>View what you’ve stored online and schedule deliveries with a tap!</>
    ),
    src: photoCatalog,
  },
];

const MOVING_STEPS: ServiceIllustration[] = [
  {
    title: 'Packing',
    description: <>We’ll professionally and safely pack your boxes.</>,
    src: wrappedPackedItems,
  },
  {
    title: 'Moving',
    description: (
      <>
        We’ll move your items into your new home and can move some of them into
        storage too.
      </>
    ),
    src: moverWithTruck,
  },
  {
    title: 'Tracking',
    description: (
      <>Track your truck in-route to your pickup and from point A to B.</>
    ),
    src: locationTruckTracking,
  },
  {
    title: 'Unpacking',
    description: <>We’ll unload items into the rooms you need them.</>,
    src: moverWithFridge,
  },
];

type Service = ServiceSelection.Storage | ServiceSelection.Moving;

const TAB_OPTIONS: {
  [key in Service]: {
    name: string;
    steps: ServiceIllustration[];
    cta: { label: string; service: Service };
    index: number;
  };
} = {
  [ServiceSelection.Storage]: {
    name: 'Storage',
    steps: STORAGE_STEPS,
    cta: { label: 'Book Storage', service: ServiceSelection.Storage },
    index: 0,
  },
  [ServiceSelection.Moving]: {
    name: 'Moving',
    steps: MOVING_STEPS,
    cta: { label: 'Book Moving', service: ServiceSelection.Moving },
    index: 1,
  },
};

export const HowClutterWorks = ({
  initialService,
  ...blockProps
}: {
  initialService: Service;
} & BlockProps) => {
  const {
    data: { serviceSelection },
  } = useClientDataContext();
  const [selectedServiceTab, setSelectedServiceTab] = useState<
    Service | undefined
  >(initialService ?? ServiceSelection.Storage);
  const track = useTrack({
    container: 'how_clutter_works',
  });

  useEffectAfterMount(() => {
    setSelectedServiceTab(
      serviceSelection === ServiceSelection.Moving
        ? ServiceSelection.Moving
        : ServiceSelection.Storage,
    );
  }, [serviceSelection]);

  const selectedServiceOptions =
    TAB_OPTIONS[selectedServiceTab ?? ServiceSelection.Storage];

  return (
    <Block {...blockProps}>
      <Box.Flex
        justifyContent={['center', null, 'space-between']}
        margin="0 0 64px"
        textAlign={['center', null, 'left']}
      >
        <Box.Flex flexDirection={['column', null, 'row']}>
          <Box margin={['0 0 16px', null, '0 32px 0 0']}>
            <Text.Title size="large">How Clutter works</Text.Title>
          </Box>
          <TabContainer
            justifyContent={['center', null, 'initial']}
            alignItems="center"
          >
            {Object.values(TAB_OPTIONS).map((option, index) => (
              <TabItem
                key={index}
                name={option.name}
                active={selectedServiceTab === option.cta.service}
                onClick={() => {
                  track({
                    action: 'click',
                    objectType: 'tab',
                    label: option.name,
                  });
                  setSelectedServiceTab(option.cta.service);
                }}
              />
            ))}
          </TabContainer>
        </Box.Flex>
        <Box display={['none', null, 'flex']}>
          <ButtonCTA
            label={selectedServiceOptions.cta.label}
            service={selectedServiceOptions.cta.service}
            container="how_clutter_works"
            entryPoint="homepage_cta"
            size="medium"
          />
        </Box>
      </Box.Flex>
      <Box.Flex
        textAlign="center"
        gap="24px"
        flexDirection={['column', null, 'row']}
      >
        {selectedServiceOptions.steps.map((step, key) => (
          <Box.FlexItem key={key} flexBasis={['100%', null, '25%']}>
            <Box margin="0 0 48px" height="110px">
              <img
                src={step.src}
                alt=""
                height="110"
                width="144"
                style={{ height: '100%' }}
              />
            </Box>
            <Box margin="0 0 8px">
              <Text.Title size="extraSmall">{step.title}</Text.Title>
            </Box>
            <Text.Body>{step.description}</Text.Body>
          </Box.FlexItem>
        ))}
      </Box.Flex>
    </Block>
  );
};
